import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, UserCircle2, X } from 'lucide-react';
import { Button } from './ui/button';
import { API } from '../App';
import { useCredits } from '../context/CreditContext';
import { useTheme } from '../context/ThemeContext';

/**
 * One-time prompt asking phone-OTP users to set their real name after
 * signup. Driven by the backend `needs_name_prompt` flag on
 * `/auth/me` — that flag is true while their name is still the
 * auto-generated `User XXXX` placeholder AND they haven't dismissed
 * it. POSTing to `/api/me/initial-name` (with or without a name)
 * permanently clears the flag.
 *
 *  Why not the OTP-gated `/me/profile-name`? They JUST verified phone
 *  to log in — re-OTP'ing them immediately after to set their initial
 *  name would be silly friction. The OTP gate kicks in for *changes*
 *  later, which the `CertNameEditor` modal handles.
 */
export default function InitialNamePrompt() {
  const { user, refreshUser } = useCredits();
  const { isDark } = useTheme();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Open only when the freshly-loaded /auth/me explicitly says so.
    // We keep the open state local (don't react to subsequent flips)
    // so a successful submit closes the modal even though `refreshUser`
    // momentarily re-flips the flag during the round-trip.
    if (user && user.needs_name_prompt) setOpen(true);
  }, [user]);

  if (!open || !user) return null;

  const finalize = async (newName) => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/me/initial-name`,
        newName ? { name: newName } : {},
        { withCredentials: true }
      );
      await refreshUser?.();
      if (newName) toast.success('Name saved');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const onSave = () => {
    const trimmed = name.trim().replace(/\s+/g, ' ');
    if (trimmed.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    finalize(trimmed);
  };

  const onSkip = () => finalize(null);

  const inputBase = `w-full rounded-[10px] border px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-brand-500/40 ${
    isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-[#0e1f35]'
  }`;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
      data-testid="initial-name-prompt-overlay"
    >
      <div
        className={`relative w-full max-w-md rounded-2xl border shadow-2xl p-6 ${
          isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
        }`}
        data-testid="initial-name-prompt"
      >
        <button
          onClick={onSkip}
          disabled={busy}
          className={`absolute top-3 right-3 p-1 rounded-md ${
            isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
          aria-label="Skip"
          data-testid="initial-name-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <UserCircle2 className="w-5 h-5 text-brand-500" />
          <h3 className={`text-[16px] font-semibold ${isDark ? 'text-white' : 'text-[#0e1f35]'}`}>
            What should we call you?
          </h3>
        </div>
        <p className={`text-[12px] mb-4 ${isDark ? 'text-gray-400' : 'text-[#4a6078]'}`}>
          We'll use this on your certificates, the leaderboard, and anywhere your
          name appears. You can change it later from the certificate preview.
        </p>

        <label
          className={`block text-[12px] font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-[#0e1f35]'
          }`}
        >
          Full name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          autoFocus
          placeholder="Jean Mukamana"
          onKeyDown={(e) => e.key === 'Enter' && !busy && onSave()}
          className={inputBase}
          data-testid="initial-name-prompt-input"
        />

        <div className="flex gap-2 mt-4">
          <Button
            variant="ghost"
            onClick={onSkip}
            disabled={busy}
            className="flex-1"
            data-testid="initial-name-prompt-skip-btn"
          >
            Skip for now
          </Button>
          <Button
            onClick={onSave}
            disabled={busy || name.trim().length < 2}
            className="flex-1 bg-brand-500 hover:bg-brand-600 text-white"
            data-testid="initial-name-prompt-save-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save name'}
          </Button>
        </div>
      </div>
    </div>
  );
}
